import { FC, useCallback, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Loader } from 'team-hero-ui';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { withErrorBoundary } from '@sentry/react';
import { joiResolver } from '@hookform/resolvers/joi';

import type { ICustomFields } from 'interfaces/CustomFields.interface';
import type { TLocale } from 'interfaces/Locale.interface';
import type { IApplicationFormState } from './JobAdApplyForm.interface';
import {
  useGetPublicSystemSettingsQuery,
  useGetPublicApplicationJobAdsQuery,
} from 'services/publicTeamHeroApi.service';
import {
  ErrorWrapper,
  JobAdApplyFormStyled,
  WrapperContainerStyled,
} from './JobAdApplyForm.styled';
import { dateService } from 'services/date/date.service';
import { useCreateApplication } from './hooks/useCreateApplication.hook';
import { applyFormConfig } from './config';
import useNotification from 'hooks/useNotification.hook';
import LayoutError from 'components/LayoutError';
import { useToggle } from 'hooks/useToggle.hook';
import { customFieldsService } from 'services/customFields/customFields.service';
import FormStep from './components/FormStep';
import SuccessStep from './components/SuccessStep';
import { removeWhiteSpaces } from 'helpers/string/string.helper';
import { jobAdSchema } from './JobAd.schema';
import { useFileMimeType } from 'hooks/useFileMimeType.hook';

const initialStep = 0;

type TUrlParams = {
  id: string;
};

const JobAdApplyForm: FC = withErrorBoundary(
  () => {
    const { t, i18n } = useTranslation();
    const { data: publicSettingsData } = useGetPublicSystemSettingsQuery();
    const { id: selectedJobAdId } = useParams<TUrlParams>();
    const [searchParams] = useSearchParams();
    const referral_Code = searchParams.get('referral_code');

    const formCustomFields = publicSettingsData as unknown as
      | ICustomFields
      | undefined;

    const { allAvailableMimeTypeValidator, imageMimeTypeValidator } =
      useFileMimeType();

    const schema = jobAdSchema(
      t,
      i18n.language as TLocale,
      formCustomFields,
      allAvailableMimeTypeValidator,
      imageMimeTypeValidator(
        publicSettingsData?.settings.application_form_image_number_required || 0
      ),
      publicSettingsData?.settings.applicant_min_age
    );

    const { handleSubmit, control, reset } = useForm<IApplicationFormState>({
      resolver:
        schema &&
        joiResolver(schema, { allowUnknown: true, abortEarly: false }),
      defaultValues: {
        dateOfBirth: null,
        usedReferralCode: referral_Code || undefined,
      },
    });

    const [currentStepIndex, setCurrentStepIndex] = useState(initialStep);

    const { set: setNotification } = useNotification();

    const {
      toggleValue: formIsSubmitted,
      on: setFormIsSubmitted,
      off: resetSubmittedForm,
    } = useToggle(false);

    const {
      data: jobAds,
      isLoading: isJobAdsLoading,
      isError,
    } = useGetPublicApplicationJobAdsQuery({ pagination: false });

    const { sections: staticSections } = applyFormConfig({
      t,
      showProfilePictureSection:
        !!publicSettingsData?.settings.application_form_image_enable_upload,
      profilePictureRequired:
        !!publicSettingsData?.settings.application_form_image_number_required &&
        publicSettingsData?.settings.application_form_image_number_required > 0,
      referralProgramEnabled:
        !!publicSettingsData?.settings.referral_program_enabled,
    });

    const formSections = staticSections;

    const sourceTitle = searchParams.get('source') || '';

    const selectedJobAd = jobAds?.items.find(
      (el) => el.id === Number(selectedJobAdId)
    );
    const sourceData = selectedJobAd?.sources.find(
      (el) => el.source.title?.toLowerCase() === sourceTitle.toLowerCase()
    );

    const goToNextStep = useCallback(() => {
      setCurrentStepIndex((prev) => prev + 1);
    }, []);

    const goToPrevStep = useCallback(() => {
      setCurrentStepIndex((prev) => prev - 1);
    }, []);

    const triggerSuccess = useCallback(() => {
      setNotification({
        type: 'success',
        message: t('notification.success'),
      });
      reset();
      setCurrentStepIndex(initialStep);
      setFormIsSubmitted();
    }, [reset, setFormIsSubmitted, setNotification, t]);

    const { createApplicationFlow, isLoading } =
      useCreateApplication(triggerSuccess);

    const onSubmit: SubmitHandler<IApplicationFormState> = useCallback(
      async (values, e) => {
        e?.preventDefault();
        const customValuesParsed = customFieldsService({
          customFields: formCustomFields,
          language: i18n.languages[0] as TLocale,
        }).parseCustomFieldApiValues(values);

        const fileValuesParsed = customFieldsService({
          customFields: formCustomFields,
          language: i18n.languages[0] as TLocale,
        }).parseCustomFieldFileApiValues(values);

        const parsedValues = {
          ...values,
          ...customValuesParsed,
          avatar: values.avatar || undefined,
          jobAd: selectedJobAd?.['@id'],
          phone: values.phone ? removeWhiteSpaces(values.phone) : null,
          dateOfBirth: dateService(values.dateOfBirth || '').format('Y-M-D'),
          source: sourceData?.source['@id'] || null,
          assignee: null,
          futureContract: null,
          stage: null,
        };

        await createApplicationFlow(parsedValues, fileValuesParsed);
      },
      [
        createApplicationFlow,
        formCustomFields,
        i18n.languages,
        selectedJobAd,
        sourceData?.source,
      ]
    );

    const steps = formSections.map((section, i) => {
      const isLastStep = i === formSections.length - 1;
      const isFirstStep = i === 0;

      return (
        <FormStep
          key={section.key}
          stepIndex={i}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          control={control}
          section={section}
          jobAds={jobAds?.items || []}
          selectedJobAd={selectedJobAd}
          sourceTitle={sourceTitle}
          onBackClick={!isFirstStep ? goToPrevStep : undefined}
          onNextClick={
            isLastStep ? handleSubmit(onSubmit) : handleSubmit(goToNextStep)
          }
          disabled={isLoading}
        />
      );
    });

    const currentStep = steps[currentStepIndex];

    if (isError) {
      throw new Error('an error occurred');
    }

    if (isJobAdsLoading) {
      return <Loader />;
    }

    if (!selectedJobAd) {
      return <p>{t('noResultsFound')}</p>;
    }

    return (
      <JobAdApplyFormStyled>
        {!formIsSubmitted ? (
          <WrapperContainerStyled>{currentStep}</WrapperContainerStyled>
        ) : (
          <SuccessStep resetForm={resetSubmittedForm} />
        )}
      </JobAdApplyFormStyled>
    );
  },
  {
    fallback: ({ eventId, resetError }) => (
      <ErrorWrapper>
        <LayoutError
          eventId={eventId}
          displayReportButton
          resetError={resetError}
        />
      </ErrorWrapper>
    ),
  }
);

export default JobAdApplyForm;
